import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import {
  collaboratorsOf,
  deleteCollaborator,
  fetchCompanyAccounts,
} from "../../../api/collaborator";
import { useProfile } from "../../../api/profile";
import {
  enumsectionToI18nKey,
  enumusertypeToI18nKey,
} from "../../../generated/enums";
import { AccountWithoutPassword } from "../../../generated/types";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import {
  isAssistant,
  landingForType,
  spaceForType,
} from "../../../utils/roles";
import { DeleteModal } from "../../common/DeleteModal";
import { EmptyState } from "../../common/EmptyState";
import { Header } from "../../Header";
import { sectionToIcon } from "../../type/default";
import {
  CollaboratorModal,
  CollaboratorModalTarget,
} from "./CollaboratorModal";
import { CollaboratorShareModal } from "./CollaboratorShareModal";

type CollaboratorCardProps = {
  account: AccountWithoutPassword;
  onEdit: (account: AccountWithoutPassword) => void;
  onDelete: (account: AccountWithoutPassword) => void;
  onShare: (account: AccountWithoutPassword) => void;
  busy: boolean;
  canShare: boolean;
};

const CollaboratorCard = ({
  account,
  onEdit,
  onDelete,
  onShare,
  busy,
  canShare,
}: CollaboratorCardProps) => {
  const { t } = useTranslation();
  const contact = [account.email, account.phone_mobile || account.phone_fixe]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="bg-white border border-grey-medium rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div className="flex items-center gap-4 min-w-0">
        <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-secondary text-tertiary">
          <FontAwesomeIcon icon="user" />
        </div>
        <div className="min-w-0">
          <p className="font-oxaniumMedium text-default-text truncate">
            {`${account.first_name} ${account.last_name}`}
          </p>
          <p className="text-sm text-tertiary truncate">{contact}</p>
          <p className="text-xs text-tertiary">
            {t(enumusertypeToI18nKey[account.type])}
            {!account.is_activated && ` · ${t("collaborator-not-activated")}`}
          </p>
        </div>
      </div>

      <div className="flex gap-2 shrink-0 self-end md:self-auto">
        {canShare && (
          <button
            type="button"
            onClick={() => onShare(account)}
            disabled={busy}
            className="px-4 py-2 rounded-lg border border-grey-medium text-tertiary font-oxaniumMedium hover:bg-grey-light transition-colors disabled:opacity-50"
          >
            <FontAwesomeIcon icon="share-nodes" className="mr-2" />
            {t("collaborator-share")}
          </button>
        )}
        <button
          type="button"
          onClick={() => onEdit(account)}
          disabled={busy}
          aria-label={t("collaborator-edit-title")}
          title={t("collaborator-edit-title")}
          className="px-3 py-2 rounded-lg border border-grey-medium text-tertiary hover:bg-grey-light transition-colors disabled:opacity-50"
        >
          <FontAwesomeIcon icon="pen" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(account)}
          disabled={busy}
          aria-label={t("collaborator-delete-title")}
          title={t("collaborator-delete-title")}
          className="px-3 py-2 rounded-lg border border-grey-medium text-tertiary hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50"
        >
          <FontAwesomeIcon icon="trash-can" />
        </button>
      </div>
    </div>
  );
};

/** Comptes de l'entreprise, gérés par l'éleveur ou l'installateur titulaire. */
export const CollaboratorsPage = () => {
  const { t } = useTranslation();
  const { getAccountStorage } = useAccountStorage();
  const accountType = getAccountStorage()?.type;
  const { profileData, error: profileError } = useProfile();
  const owner = profileData?.account;

  const [accounts, setAccounts] = useState<AccountWithoutPassword[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalTarget, setModalTarget] =
    useState<CollaboratorModalTarget | null>(null);
  const [toDelete, setToDelete] = useState<AccountWithoutPassword | null>(null);
  const [toShare, setToShare] = useState<AccountWithoutPassword | null>(null);
  const [busyId, setBusyId] = useState(0);

  const reload = useCallback(async () => {
    const result = await fetchCompanyAccounts();
    setAccounts(result.accounts);
    setError(result.error && t(result.error));
    setLoading(false);
  }, [t]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Un collaborateur ne gère pas ses pairs : USERS le lui refuserait de toute façon.
  if (isAssistant(accountType)) {
    return <Navigate to={landingForType(accountType)} replace />;
  }

  const collaborators = owner ? collaboratorsOf(accounts, owner.id) : [];

  const handleConfirmDelete = async () => {
    if (!toDelete) return;
    const id = toDelete.id;
    setToDelete(null);
    setBusyId(id);
    setError("");
    const result = await deleteCollaborator(id);
    setBusyId(0);
    if (result) {
      setError(t(result));
      return;
    }
    await reload();
  };

  const displayedError = error || profileError || "";

  return (
    <div className="p-4">
      <Header
        title={t(enumsectionToI18nKey["section_collaborators"])}
        icon={sectionToIcon.section_collaborators}
        buttonText={owner ? t("collaborator-create-title") : undefined}
        onButtonClick={() => setModalTarget({ mode: "create" })}
      />

      {displayedError && (
        <div className="mt-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
          {displayedError}
        </div>
      )}

      {loading || !owner ? (
        !displayedError && <p className="mt-6 text-tertiary">{t("loading")}</p>
      ) : collaborators.length === 0 ? (
        <EmptyState
          icon="user-group"
          title={t("collaborators-empty-title")}
          description={t("collaborators-empty-text")}
        />
      ) : (
        <section className="mt-6">
          <h2 className="text-lg font-oxaniumMedium text-default-text mb-3">
            {t("collaborators-list-title")} ({collaborators.length})
          </h2>
          <div className="flex flex-col gap-3">
            {collaborators.map((account) => (
              <CollaboratorCard
                key={account.id}
                account={account}
                onEdit={(account) => setModalTarget({ mode: "edit", account })}
                onDelete={setToDelete}
                onShare={setToShare}
                busy={busyId === account.id}
                // L'installateur affecte ses collaborateurs depuis la page Partenaires.
                canShare={spaceForType(accountType) === "breeder"}
              />
            ))}
          </div>
        </section>
      )}

      {owner && modalTarget && (
        <CollaboratorModal
          target={modalTarget}
          owner={owner}
          onClose={() => setModalTarget(null)}
          onSuccess={reload}
        />
      )}

      {toShare && (
        <CollaboratorShareModal
          account={toShare}
          onClose={() => setToShare(null)}
        />
      )}

      <DeleteModal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        title={t("collaborator-delete-title")}
        warningText={t("collaborator-delete-text", {
          name: toDelete ? `${toDelete.first_name} ${toDelete.last_name}` : "",
        })}
        handleConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
};
